// src/viqai/AffiliateAdmin.jsx
// ─── Gestionnaire commercial du programme partenaires ───────────────────────
// Quatre onglets : Apporteurs · Codes · Ventes · Commissions.
//
// Un parti pris d'affichage qui vaut d'être explicité (§47) : les deux
// pourcentages ne sont JAMAIS affichés côte à côte sans libellé. « 30 % » et
// « 40 % » l'un à côté de l'autre sont indiscernables, alors que le premier est
// un taux sur la vente et le second une part du pool. Ils sont donc toujours
// nommés « Commission sur vente » et « Part du pool ».
//
// L'écriture passe exclusivement par /api/affiliate-admin, qui revalide tout.
// Aucun calcul de commission n'est fait ici : le simulateur interroge le
// serveur, pour que ce qui est prévisualisé soit exactement ce qui sera versé.

import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiPatch } from "./api";
import "./viqai-dark.css";

const eur = (c) => `${((c || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const ROLE_LABELS = {
  calling: "Calling", qualification: "Qualification", closing: "Closing",
  partner: "Partenaire", manager: "Manager", custom: "Autre",
};

function Field({ label, hint, children }) {
  return (
    <label className="aff-field">
      <span className="aff-label">{label}</span>
      {children}
      {hint && <span className="aff-hint">{hint}</span>}
    </label>
  );
}

// ── Éditeur de code ─────────────────────────────────────────────────────────
function CodeEditor({ code, members, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    code: code?.code || "",
    name: code?.name || "",
    commission_first_pct: code?.commission_first_pct ?? 30,
    commission_renew_pct: code?.commission_renew_pct ?? 15,
    renewal_split_same: code?.renewal_split_same !== false,
    active: code?.active !== false,
  }));
  const [parts, setParts] = useState(() =>
    (code?.participants || []).map(p => ({
      member_id: p.member_id, role: p.role || "partner",
      split_pct: Number(p.split_pct), renewal_split_pct: p.renewal_split_pct == null ? "" : Number(p.renewal_split_pct),
      name: p.name,
    }))
  );
  const [sim, setSim] = useState(null);
  const [simAmount, setSimAmount] = useState(100000);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const total = parts.reduce((s, p) => s + (Number(p.split_pct) || 0), 0);
  const totalRenew = parts.reduce((s, p) => s + (Number(p.renewal_split_pct || p.split_pct) || 0), 0);
  const totalOk = Math.abs(total - 100) < 0.01;
  const renewOk = form.renewal_split_same || Math.abs(totalRenew - 100) < 0.01;

  const addPart = () => {
    const used = parts.map(p => p.member_id);
    const free = members.find(m => !used.includes(m.id) && m.status === "active");
    if (!free) return;
    setParts(p => [...p, { member_id: free.id, name: free.name, role: "partner", split_pct: 0, renewal_split_pct: "" }]);
  };

  const simulate = async () => {
    const j = await apiPost("/api/affiliate-admin/simulate", {
      code_id: code?.id || null,
      amount_cents: Number(simAmount),
      sale_type: "first_sale",
      commission_pct: Number(form.commission_first_pct),
      participants: parts.map(p => ({ member_id: p.member_id, name: p.name, role: p.role, split_pct: Number(p.split_pct) })),
    }).catch(() => null);
    setSim(j?.simulation || null);
  };

  const save = async () => {
    setError(null); setSaving(true);
    const body = {
      ...form,
      participants: parts.map(p => ({
        member_id: p.member_id, role: p.role,
        split_pct: Number(p.split_pct),
        renewal_split_pct: form.renewal_split_same || p.renewal_split_pct === "" ? null : Number(p.renewal_split_pct),
      })),
    };
    const j = code?.id
      ? await apiPatch(`/api/affiliate-admin/codes/${code.id}`, body).catch(e => ({ error: e.message }))
      : await apiPost("/api/affiliate-admin/codes", body).catch(e => ({ error: e.message }));
    setSaving(false);
    if (j?.success) onSave(); else setError(j?.error || "Enregistrement impossible.");
  };

  return (
    <div className="aff-editor">
      <div className="aff-editor-head">{code?.id ? `Modifier ${code.code}` : "Créer un code partenaire"}</div>

      <div className="aff-grid2">
        <Field label="Code">
          <input value={form.code} disabled={!!code?.id}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="AUTO75" />
        </Field>
        <Field label="Nom interne">
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Campagne Septembre Nord" />
        </Field>
        <Field label="Commission sur vente — première vente" hint="Part du montant encaissé reversée au programme">
          <input type="number" min="0" max="50" step="0.5" value={form.commission_first_pct}
            onChange={e => setForm(f => ({ ...f, commission_first_pct: e.target.value }))} />
        </Field>
        <Field label="Commission sur vente — renouvellement">
          <input type="number" min="0" max="50" step="0.5" value={form.commission_renew_pct}
            onChange={e => setForm(f => ({ ...f, commission_renew_pct: e.target.value }))} />
        </Field>
      </div>

      <div className="aff-sub">Participants — répartition du pool</div>
      <div className="aff-hint" style={{ marginBottom: ".6rem" }}>
        Ces parts se partagent la commission, pas le chiffre d'affaires.
      </div>

      {parts.map((p, i) => (
        <div key={i} className="aff-part">
          <select value={p.member_id}
            onChange={e => { const v = parseInt(e.target.value); const m = members.find(x => x.id === v);
              setParts(a => a.map((x, k) => k === i ? { ...x, member_id: v, name: m?.name } : x)); }}>
            {members.map(m => <option key={m.id} value={m.id}>{m.name}{m.status !== "active" ? ` (${m.status})` : ""}</option>)}
          </select>
          <select value={p.role}
            onChange={e => setParts(a => a.map((x, k) => k === i ? { ...x, role: e.target.value } : x))}>
            {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <div className="aff-pct">
            <input type="number" min="0" max="100" step="1" value={p.split_pct}
              onChange={e => setParts(a => a.map((x, k) => k === i ? { ...x, split_pct: e.target.value } : x))} />
            <span>% du pool</span>
          </div>
          {!form.renewal_split_same && (
            <div className="aff-pct">
              <input type="number" min="0" max="100" step="1" placeholder="idem"
                value={p.renewal_split_pct}
                onChange={e => setParts(a => a.map((x, k) => k === i ? { ...x, renewal_split_pct: e.target.value } : x))} />
              <span>% renouv.</span>
            </div>
          )}
          <button type="button" className="aff-x" onClick={() => setParts(a => a.filter((_, k) => k !== i))}>✕</button>
        </div>
      ))}

      <button type="button" className="viq-btn" onClick={addPart}>+ Ajouter un participant</button>

      <div className={`aff-total${totalOk ? " ok" : " ko"}`}>
        Total : {total.toFixed(2)} % {totalOk ? "✓" : "— la répartition doit totaliser exactement 100 %"}
      </div>

      <label className="aff-check">
        <input type="checkbox" checked={form.renewal_split_same}
          onChange={e => setForm(f => ({ ...f, renewal_split_same: e.target.checked }))} />
        Répartition des renouvellements identique à la première vente
      </label>
      {!form.renewal_split_same && (
        <div className={`aff-total${renewOk ? " ok" : " ko"}`}>
          Total renouvellements : {totalRenew.toFixed(2)} % {renewOk ? "✓" : "— doit totaliser 100 %"}
        </div>
      )}

      {/* §57 — Vérifier avant d'enregistrer. Le calcul vient du serveur : ce qui
          est prévisualisé est exactement ce qui sera versé. */}
      <div className="aff-sub">Simulation</div>
      <div className="aff-sim-row">
        <input type="number" value={simAmount} step="1000"
          onChange={e => setSimAmount(e.target.value)} />
        <span className="aff-hint">centimes ({eur(simAmount)})</span>
        <button type="button" className="viq-btn" onClick={simulate}>Simuler</button>
      </div>
      {sim && (
        <div className="aff-sim">
          {sim.ok ? (
            <>
              <div className="viq-row"><span className="k">Montant encaissé</span><span className="v">{eur(sim.grossAmountCents)}</span></div>
              <div className="viq-row"><span className="k">Commission sur vente</span><span className="v">{sim.globalRatePct} %</span></div>
              <div className="viq-row"><span className="k">Pool de commission</span><span className="v">{eur(sim.commissionPoolCents)}</span></div>
              {sim.allocations.map((a, i) => (
                <div key={i} className="viq-row">
                  <span className="k">{a.name || `#${a.memberId}`} — {ROLE_LABELS[a.role] || a.role} — {a.splitPct} % du pool</span>
                  <span className="v pos">{eur(a.commissionCents)}</span>
                </div>
              ))}
            </>
          ) : <div className="aff-error">{sim.message || "Simulation impossible : vérifiez la répartition."}</div>}
        </div>
      )}

      {error && <div className="aff-error">{error}</div>}
      <div className="viq-actions">
        <button className="viq-btn primary" disabled={!totalOk || !renewOk || saving} onClick={save}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button className="viq-btn" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

// ── Écran principal ─────────────────────────────────────────────────────────
export default function AffiliateAdmin({ onClose }) {
  const [tab, setTab] = useState("codes");
  const [members, setMembers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [sales, setSales] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [m, c, s, p, st] = await Promise.all([
      apiGet("/api/affiliate-admin/members").catch(() => null),
      apiGet("/api/affiliate-admin/codes").catch(() => null),
      apiGet("/api/affiliate-admin/sales").catch(() => null),
      apiGet("/api/affiliate-admin/payouts").catch(() => null),
      apiGet("/api/affiliate-admin/settings").catch(() => null),
    ]);
    const pl = await apiGet("/api/affiliate-admin/pipeline").catch(() => null);
    setPipeline(pl || null);
    setMembers(m?.members || []); setCodes(c?.codes || []);
    setSales(s?.sales || []); setPayouts(p?.payouts || []);
    setSettings(st?.settings || null);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveMember = async (id, patch) => {
    const j = await apiPatch(`/api/affiliate-admin/members/${id}`, patch).catch(e => ({ error: e.message }));
    if (j?.success) load(); else alert(j?.error || "Modification impossible.");
  };

  const markPaid = async (row, status) => {
    const j = await apiPost("/api/affiliate-admin/payouts", {
      member_id: row.member_id, period: row.period, status,
    }).catch(e => ({ error: e.message }));
    if (j?.success) load(); else alert(j?.error || "Opération impossible.");
  };

  return (
    <div className="aff-admin viq-dark">
      <div className="aff-head">
        <h2>Programme partenaires</h2>
        {onClose && <button className="viq-tool-btn" onClick={onClose} aria-label="Fermer">✕</button>}
      </div>

      <div className="aff-tabs">
        {[["codes", "Codes"], ["members", "Apporteurs"], ["sales", "Ventes"], ["payouts", "Commissions"], ["pipeline", "Pipeline"], ["settings", "Réglages"]].map(([k, l]) => (
          <button key={k} className={`aff-tab${tab === k ? " on" : ""}`} onClick={() => { setTab(k); setEditing(null); }}>{l}</button>
        ))}
      </div>

      {loading && <div className="viq-empty">Chargement…</div>}

      {!loading && tab === "codes" && (editing !== null ? (
        <CodeEditor code={editing} members={members}
          onSave={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      ) : (
        <>
          <button className="viq-btn primary" onClick={() => setEditing({})}>+ Créer un code</button>
          {!codes.length && <div className="viq-empty">Aucun code pour le moment.</div>}
          {codes.map(c => (
            <div key={c.id} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-code">{c.code}</span>
                <span className="aff-name">{c.name}</span>
                {!c.active && <span className="viq-chip warn">désactivé</span>}
                <button className="viq-btn" onClick={() => setEditing(c)}>Modifier</button>
              </div>
              <div className="viq-rows">
                <div className="viq-row">
                  <span className="k">Commission sur vente</span>
                  <span className="v">{c.commission_first_pct ?? "défaut"} % · renouv. {c.commission_renew_pct ?? "défaut"} %</span>
                </div>
                {(c.participants || []).map(p => (
                  <div key={p.id} className="viq-row">
                    <span className="k">{p.name} — {ROLE_LABELS[p.role] || p.role}</span>
                    <span className="v">{Number(p.split_pct)} % du pool</span>
                  </div>
                ))}
                <div className="viq-row">
                  <span className="k">{c.clients} client(s) · CA {eur(c.ca_cents)}</span>
                  <span className="v">{eur(c.commission_cents)} de commission</span>
                </div>
              </div>
            </div>
          ))}
        </>
      ))}

      {!loading && tab === "members" && (
        <>
          {members.map(m => (
            <div key={m.id} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-name">{m.name}</span>
                <span className="viq-chip">{ROLE_LABELS[m.role] || m.role}</span>
                {m.status !== "active" && <span className="viq-chip warn">{m.status}</span>}
                <select value={m.status} onChange={e => saveMember(m.id, { status: e.target.value })}>
                  <option value="active">Actif</option>
                  <option value="suspended">Suspendu</option>
                  <option value="archived">Archivé</option>
                </select>
              </div>
              <div className="viq-rows">
                <div className="viq-row"><span className="k">Codes</span><span className="v">{m.codes || "—"}</span></div>
                <div className="viq-row"><span className="k">{m.sales} vente(s)</span><span className="v">{eur(m.commission_cents)} générés</span></div>
                <div className="viq-row"><span className="k">Déjà versé</span><span className="v">{eur(m.paid_cents)}</span></div>
                <div className="viq-row">
                  <span className="k">Taux personnels (codes individuels)</span>
                  <span className="v">
                    <input type="number" min="0" max="50" placeholder="défaut" defaultValue={m.commission_first_pct_override ?? ""}
                      onBlur={e => saveMember(m.id, { commission_first_pct_override: e.target.value === "" ? null : e.target.value })}
                      style={{ width: 62 }} /> % /{" "}
                    <input type="number" min="0" max="50" placeholder="défaut" defaultValue={m.commission_renew_pct_override ?? ""}
                      onBlur={e => saveMember(m.id, { commission_renew_pct_override: e.target.value === "" ? null : e.target.value })}
                      style={{ width: 62 }} /> %
                  </span>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {!loading && tab === "sales" && (
        <>
          {!sales.length && <div className="viq-empty">Aucune vente enregistrée.</div>}
          {sales.map(s => (
            <div key={s.id} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-code">{s.affiliate_code}</span>
                <span className="aff-name">{s.plan_id}</span>
                <span className="viq-chip">{s.sale_type === "recurrence" ? "Renouvellement" : "Première vente"}</span>
              </div>
              <div className="viq-rows">
                <div className="viq-row"><span className="k">{s.client_email || s.client_uid}</span><span className="v">{eur(s.amount_cents)}</span></div>
                <div className="viq-row">
                  <span className="k">Commission sur vente {(Number(s.commission_rate) * 100).toFixed(0)} %</span>
                  <span className="v">{eur(s.commission_cents)} de pool</span>
                </div>
                {(s.splits || []).map(sp => (
                  <div key={sp.id} className="viq-row">
                    <span className="k">{sp.name} — {ROLE_LABELS[sp.role] || sp.role} — {Number(sp.split_pct_used)} % du pool</span>
                    <span className="v pos">{eur(sp.commission_cents)}</span>
                  </div>
                ))}
                {!(s.splits || []).length && (
                  <div className="viq-row"><span className="k">Répartition</span><span className="v warn">ancien calcul</span></div>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {!loading && tab === "payouts" && (
        <>
          {!payouts.length && <div className="viq-empty">Aucune commission à verser.</div>}
          {payouts.map((p, i) => (
            <div key={i} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-name">{p.name}</span>
                <span className="viq-chip">{p.period}</span>
                <span className={`viq-chip${p.status === "paid" ? "" : " warn"}`}>{p.status === "paid" ? "versé" : "en attente"}</span>
              </div>
              <div className="viq-rows">
                <div className="viq-row"><span className="k">{p.sales} vente(s)</span><span className="v">{eur(p.due_cents)}</span></div>
                {p.paid_at && <div className="viq-row"><span className="k">Versé le</span><span className="v">{new Date(p.paid_at).toLocaleDateString("fr-FR")}</span></div>}
              </div>
              {/* §38 — Chaque participant est réglé indépendamment : Marie peut
                  être payée pendant que Thomas reste en attente. */}
              <div className="viq-actions">
                {p.status !== "paid"
                  ? <button className="viq-btn primary" onClick={() => markPaid(p, "paid")}>Marquer versé</button>
                  : <button className="viq-btn" onClick={() => markPaid(p, "pending")}>Remettre en attente</button>}
              </div>
            </div>
          ))}
        </>
      )}

      {!loading && tab === "pipeline" && (
        <>
          <div className="aff-hint" style={{ marginBottom: ".8rem" }}>
            Compté sur les attributions d'étape, pas sur les commissions : un lead
            qualifié qui ne signe pas reste un lead qualifié, et c'est ce qu'on veut mesurer.
          </div>
          {!(pipeline?.kpi || []).length && <div className="viq-empty">Aucune attribution enregistrée.</div>}
          {(pipeline?.kpi || []).map(k => (
            <div key={k.id} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-name">{k.name}</span>
                <span className="viq-chip">{ROLE_LABELS[k.role] || k.role}</span>
              </div>
              <div className="viq-rows">
                <div className="viq-row"><span className="k">Leads générés</span><span className="v">{k.leads}</span></div>
                <div className="viq-row"><span className="k">Qualifiés</span><span className="v">{k.qualifies}</span></div>
                <div className="viq-row"><span className="k">Signatures</span><span className="v pos">{k.signatures}</span></div>
                {k.taux_qualification != null && <div className="viq-row"><span className="k">Taux de qualification</span><span className="v">{k.taux_qualification} %</span></div>}
                {k.taux_closing != null && <div className="viq-row"><span className="k">Taux de closing</span><span className="v">{k.taux_closing} %</span></div>}
              </div>
            </div>
          ))}
          {(pipeline?.deals || []).length > 0 && <div className="aff-sub">Opportunités récentes</div>}
          {(pipeline?.deals || []).slice(0, 15).map(d => (
            <div key={d.id} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-name">{d.client_uid || `Lead #${d.lead_id}`}</span>
                {d.code && <span className="aff-code">{d.code}</span>}
                <span className="viq-chip">{d.stage}</span>
                {d.qualification_score && <span className="viq-chip accent">{d.qualification_score}</span>}
                {d.participants_override && <span className="viq-chip warn">répartition spécifique</span>}
              </div>
              <div className="viq-rows">
                {d.caller_name && <div className="viq-row"><span className="k">Calling</span><span className="v">{d.caller_name}</span></div>}
                {d.qualifier_name && <div className="viq-row"><span className="k">Qualification</span><span className="v">{d.qualifier_name}</span></div>}
                {d.closer_name && <div className="viq-row"><span className="k">Closing</span><span className="v">{d.closer_name}</span></div>}
              </div>
            </div>
          ))}
        </>
      )}

      {!loading && tab === "settings" && settings && (
        <div className="aff-card">
          <div className="aff-sub">Valeurs par défaut du programme</div>
          <div className="aff-hint" style={{ marginBottom: ".8rem" }}>
            Appliquées quand un code ne définit pas son propre taux. Modifiables sans redéploiement.
          </div>
          <div className="aff-grid2">
            {[
              ["commission_first_pct", "Commission par défaut — première vente"],
              ["commission_renew_pct", "Commission par défaut — renouvellement"],
              ["bonus_pct", "Bonus volume"],
              ["bonus_threshold", "Seuil du bonus (signatures / mois)"],
              ["commission_max_pct", "Plafond de sécurité"],
            ].map(([k, label]) => (
              <Field key={k} label={label}>
                <input type="number" defaultValue={settings[k]}
                  onBlur={async e => {
                    const j = await apiPatch("/api/affiliate-admin/settings", { [k]: e.target.value }).catch(() => null);
                    if (j?.success) setSettings(j.settings);
                  }} />
              </Field>
            ))}
          </div>
          <div className="aff-hint">
            Le plafond bloque toute saisie au-dessus : il évite qu'une erreur de frappe
            enregistre une commission à 300 %.
          </div>
        </div>
      )}
    </div>
  );
}
